import { pushDataLayer } from './tracking';

/**
 * Consent bridge: ccm19 → Matomo Tag Manager.
 *
 * Matomo runs "consent required", so its MTM tags must not fire until the
 * visitor accepts the analytics embedding in ccm19. ccm19 dispatches the
 * `ccm19EmbeddingAccepted` CustomEvent on `window` for every accepted embedding
 * — on live opt-in *and* repeated on each page load once consent is stored — so
 * a single listener covers both returning and first-time visitors. We forward
 * the relevant one as a `consent_analytics_granted` event on the Matomo
 * dataLayer (`window._mtm`); the MTM container uses it to release the tracking
 * tags (see head-scripts.twig and the MTM companion config).
 *
 * This file is a global kill switch for *all* analytics, and every way it can
 * fail is silent, so it is deliberately defensive: it accepts both documented
 * shapes of `CCM.acceptedEmbeddings`, polls for a late-initialising ccm19, never
 * throws into its caller, and can explain itself via `?mtmDebug=1`.
 *
 * NOTE: ANALYTICS_EMBEDDING must match the Matomo embedding's name in the ccm19
 * dashboard. It is matched case-insensitively as a substring, so "Matomo",
 * "Matomo Analytics" and "Matomo Tag Manager" all resolve.
 */
const ANALYTICS_EMBEDDING = 'matomo';

/** How long to keep looking for a ccm19 that initialises after we do. */
const POLL_INTERVAL_MS = 250;
const POLL_TIMEOUT_MS = 10000;

interface Embedding {
  id?: string;
  name?: string;
}

interface CcmGlobal {
  // Documented inconsistently by ccm19 — an array in some versions, a getter
  // method in others. Handle both.
  acceptedEmbeddings?: Embedding[] | (() => Embedding[]);
}

const debugEnabled = (): boolean => {
  try {
    return new URLSearchParams(window.location.search).has('mtmSetDebugFlag');
  } catch {
    return false;
  }
};

let debug = false;
const log = (...args: unknown[]): void => {
  if (debug) console.info('[consent]', ...args);
};

const isAnalytics = (name?: string): boolean =>
  !!name && name.toLowerCase().includes(ANALYTICS_EMBEDDING);

const acceptedEmbeddings = (): Embedding[] => {
  const ccm = (window as unknown as { CCM?: CcmGlobal }).CCM;
  if (!ccm) return [];

  const raw = ccm.acceptedEmbeddings;
  const list = typeof raw === 'function' ? raw.call(ccm) : raw;

  return Array.isArray(list) ? list : [];
};

const analyticsAlreadyAccepted = (): boolean => {
  const list = acceptedEmbeddings();
  log(
    'accepted embeddings:',
    list.map((emb) => emb?.name),
  );
  return list.some((emb) => isAnalytics(emb?.name));
};

let granted = false;
const grantOnce = (source: string): void => {
  if (granted) return;
  granted = true;
  log('consent granted via', source, '→ pushing consent_analytics_granted');
  pushDataLayer({ event: 'consent_analytics_granted' });
};

const start = (): void => {
  debug = debugEnabled();
  log('init; CCM present:', 'CCM' in window);

  // Live opt-in, plus the per-page-load repeat that ccm19 emits for stored consent.
  window.addEventListener('ccm19EmbeddingAccepted', (event) => {
    const name = (event as CustomEvent<{ name?: string }>).detail?.name;
    log('ccm19EmbeddingAccepted:', name);
    if (isAnalytics(name)) grantOnce('ccm19EmbeddingAccepted');
  });

  // ccm19 may have initialised before this module ran, so the event above could
  // have fired already: re-check on its ready signal, and immediately too.
  window.addEventListener('ccm19WidgetLoaded', () => {
    if (analyticsAlreadyAccepted()) grantOnce('ccm19WidgetLoaded');
  });

  if (analyticsAlreadyAccepted()) {
    grantOnce('initial probe');
    return;
  }

  // ...and if ccm19 shows up *after* its own ready event, neither of the above
  // fires. Poll briefly as a last resort, then give up quietly.
  const startedAt = Date.now();
  const timer = window.setInterval(() => {
    if (granted || Date.now() - startedAt > POLL_TIMEOUT_MS) {
      if (!granted) log('gave up polling; analytics consent not detected');
      window.clearInterval(timer);
      return;
    }
    if (analyticsAlreadyAccepted()) {
      grantOnce('poll');
      window.clearInterval(timer);
    }
  }, POLL_INTERVAL_MS);
};

export const initConsent = (): void => {
  if (typeof window === 'undefined') return;

  // Called from index.ts before the Vue app mounts: a throw in here would take
  // the whole bundle down with it. Losing analytics must not lose the site.
  try {
    start();
  } catch (error) {
    console.error('[consent] bridge failed to initialise', error);
  }
};
